import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
  Card, CardHeader, CardContent, IconButton, Tooltip,
  Typography, Divider, Box, CardActions, Button, CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import AddServiceDialog from './AddServiceDialog';
import { getLinkedServices, getServicesAddedToBill } from '../API/LinkedServicesApi';
import { addServiceToBillApi, removeService } from '../API/BillingApi';
import AuthContext from '../Context/AuthProvider';
import { useSnackbar } from '../Utility/UserMessageContext';
import { numberToWords } from '../Utility/Utility';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import Grid2 from '@mui/material/Grid2';
const ServiceTableSection = ({
  billId,
  visitId,
  patientId,
  onServiceDataChange,
  isSmallTablet,
  onTotalBilledChange,
}) => {
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();

  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [openServiceDialog, setOpenServiceDialog] = useState(false);
  const [linkedServices, setLinkedServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [discountInPercentage, setDiscountInPercentage] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [serviceToDeleteId, setServiceToDeleteId] = useState(null);

  const fetchServicesForBill = useCallback(async () => {
    if (!billId) {
      setServices([]);
      return;
    }

    setLoadingServices(true);
    try {
      const result = await getServicesAddedToBill(auth, billId);
      if (result.isSuccess) {
        setServices(result.data || []);
      } else {
        const msg = typeof result.data === 'string' ? result.data : result.data?.title || "Failed to fetch services.";
        showSnackbar?.(msg, "error");
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      showSnackbar?.("Error fetching services.", "error");
    } finally {
      setLoadingServices(false);
    }
  }, [auth, billId, showSnackbar]);

  const fetchLinkedServices = useCallback(async () => {
    if (!visitId && !patientId) return;

    try {
      const result = await getLinkedServices(auth, visitId || patientId);
      if (result.isSuccess) {
        setLinkedServices(result.data || []);
      } else {
        showSnackbar(result.data || "Failed to fetch linked services.", "error");
      }
    } catch (error) {
      console.error("Error fetching linked services:", error);
      showSnackbar("An unexpected error occurred while fetching linked services.", "error");
    } finally {
      setLoadingServices(false);
    }
  }, [auth, visitId, patientId, showSnackbar]);

  useEffect(() => {
    fetchServicesForBill();
  }, [fetchServicesForBill]);

  const handleOpenServiceDialog = async () => {
    setOpenServiceDialog(true);
    await fetchLinkedServices();
  };

  const handleCloseServiceDialog = () => {
    setOpenServiceDialog(false);
    setSelectedServiceId("");
    setSelectedService(null);
    setDiscountInPercentage(0);
    setLinkedServices([]);
  };

  const handleSaveService = async () => {
    if (!selectedServiceId || !billId) {
      showSnackbar("Missing service or bill ID.", "error");
      return;
    }
    setIsSaving(true);
    try {
      const response = await addServiceToBillApi(auth, billId, selectedServiceId, discountInPercentage);

      if (response.isSuccess) {
        showSnackbar("Service added successfully!", "success");
        handleCloseServiceDialog();
        await fetchServicesForBill();
        onServiceDataChange?.();
      } else {
        showSnackbar(response.data || "Failed to add service.", "error");
      }
    } catch (error) {
      console.error("Error while adding service:", error);
      showSnackbar("Error adding service.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteService = (serviceId) => {
    setServiceToDeleteId(serviceId);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteService = async () => {
    if (!serviceToDeleteId) return;

    try {
      const response = await removeService(auth, serviceToDeleteId);
      if (response.isSuccess) {
        showSnackbar("Service removed successfully!", "success");
        await fetchServicesForBill();
        onServiceDataChange?.();
      } else {
        showSnackbar(response.data || "Failed to remove service.", "error");
      }
    } catch (error) {
      console.error("Error removing service:", error);
      showSnackbar("An error occurred while removing service.", "error");
    } finally {
      setOpenDeleteDialog(false);
      setServiceToDeleteId(null);
    }
  };

  const totalAmount = useMemo(() => {
    const sum = services.reduce(
      (acc, service) =>
        acc + (service.fees - (service.fees * (service.discountInPercentage || 0)) / 100),
      0
    );
    return sum.toFixed(2);
  }, [services]);

  useEffect(() => {
    onTotalBilledChange?.(parseFloat(totalAmount));
  }, [totalAmount, onTotalBilledChange]);

  const totalRow = useMemo(() => ({
    id: "TOTAL",
    serviceCode: "",
    serviceName:
      totalAmount === "0.00"
        ? "Total Billed Amount (INR Zero only)"
        : `Total Billed Amount (INR ${numberToWords(totalAmount)} only)`,
    dateOfService: "",
    fees: "",
    discountInPercentage: "",
    tax: "",
    amount: totalAmount,
  }), [totalAmount]);

  const renderCards = (data) => {
    if (!data || data.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', p: 2 }}>
          No services found.
        </Typography>
      );
    }

    return (
      <Grid2 container spacing={2} sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
      <Box sx={{ p: 1,  }}>
        {data.filter(row => row.id !== 'TOTAL').map(row => (
          <Card key={row.id} variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6">{row.serviceName} ({row.serviceCode})</Typography>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="body2">Date: {dayjs(row.scheduledStartDateTime).format("DD MMMYYYY")}</Typography>
              <Typography variant="body2"><strong>Billable</strong>: <strong>Rs {row.fees}</strong></Typography>
              <Typography variant="body2">Discount: {row.discountInPercentage}%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Amount: Rs {(row.fees - (row.fees * (row.discountInPercentage || 0)) / 100).toFixed(2)}
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button size="small" variant="contained" color="error" onClick={() => handleDeleteService(row.id)}>
                <DeleteOutlineIcon fontSize="small" />
              </Button>
            </CardActions>
          </Card>
        ))}
        {totalRow && (
          <Card variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6">{totalRow.serviceName}</Typography>
              <Typography variant="h5" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                Rs {totalRow.amount}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
      </Grid2>
    );
  };

  const columns = [
    { field: 'serviceCode', headerName: 'Service Code', width: 150 },
    {
      field: 'name',
      headerName: 'Service Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const row = params.row;
        return row?.id === 'TOTAL'
          ? <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.serviceName}</Typography>
          : row.serviceName;
      },
    },
    {
      field: 'scheduledStartDateTime',
      headerName: 'Date of Service',
      width: 150,
      renderCell: (params) =>
        params.value ? dayjs(params.value).format("DD MMMYYYY").toUpperCase() : "",
    },
    { field: 'fees', headerName: 'Billable Amount', width: 150 },
    { field: 'discountInPercentage', headerName: 'Discount', width: 150 },
    { field: 'tax', headerName: 'Tax', width: 150 },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 150,
      renderCell: (params) => {
        const row = params.row;
        if (row?.id === 'TOTAL') {
          return <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Rs {row.amount}</Typography>;
        }
        const discount = row.discountInPercentage || 0;
        const finalAmount = row.fees - (row.fees * discount) / 100;
        return `Rs ${finalAmount.toFixed(2)}`;
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => {
        const row = params.row;
        if (row?.id === 'TOTAL') return null;
        return (
          <Tooltip title="Remove" arrow>
            <IconButton onClick={() => handleDeleteService(row.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      }
    }
  ];

  return (
    <Card variant="outlined" sx={{ width: "100%", borderStyle: "none" }}>
      <CardHeader
        subheader="Services"
        subheaderTypographyProps={{ variant: "h6" }}
        sx={{ paddingY: "10px" }}
        action={
          <Tooltip title="New Service" arrow>
            <IconButton color="secondary" size="small" onClick={handleOpenServiceDialog}>
              <AddRoundedIcon />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent sx={{ height: isSmallTablet ? 'auto' : '300px', paddingTop: "0px", maxHeight: "calc(100vh - 200px)", overflowY: "auto"}}>
        {isSmallTablet
          ? renderCards(services)
          : (
            <DataGrid
              sx={{ height: "100%" }}
              initialState={{ density: "compact" }}
              rows={[...services, totalRow]}
              columns={columns}
              getRowId={(row) => row.id}
              hideFooter
              disableColumnFilter
              disableColumnMenu
              disableColumnSorting
              loading={loadingServices} // ✅ Enables skeleton rows while loading
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton"
                }
              }}
            />
          )}
      </CardContent>

      <AddServiceDialog
        open={openServiceDialog}
        onClose={handleCloseServiceDialog}
        onSave={handleSaveService}
        linkedServices={linkedServices}
        selectedServiceId={selectedServiceId}
        setSelectedServiceId={setSelectedServiceId}
        selectedService={selectedService}
        setSelectedService={setSelectedService}
        discountInPercentage={discountInPercentage}
        setDiscountInPercentage={setDiscountInPercentage}
        loading={loadingServices}
        isSaving={isSaving}
        visitId={visitId}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDeleteService}
      />
    </Card>
  );
};

export default ServiceTableSection;